import { AlertTriangle, AlertOctagon, Info, X } from 'lucide-react';
import {
  useActiveBanners,
  useDismissBanner,
  type ActiveBanner,
} from '../../../hooks/use-system-banners';

/**
 * Renders any active system banners targeting the current user's org.
 * Mounted near the top of <AppShell>. Each banner is dismissible per-user
 * (dismissals persisted in `system_broadcast_dismissals`).
 */
export function SystemBannerBar() {
  const { data: banners } = useActiveBanners();
  const dismiss = useDismissBanner();

  if (!banners || banners.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '6px 12px 0',
      }}
    >
      {banners.map((b) => (
        <BannerRow key={b.id} banner={b} onDismiss={() => dismiss.mutate(b.id)} />
      ))}
    </div>
  );
}

const SEVERITY_CONFIG = {
  INFO: {
    bg: 'var(--vz-primary-bg-subtle)',
    border: 'rgba(70, 138, 212, 0.4)',
    fg: 'var(--vz-link-color)',
    icon: <Info size={16} />,
  },
  WARNING: {
    bg: 'var(--vz-warning-bg-subtle)',
    border: 'rgba(232, 188, 82, 0.5)',
    fg: '#c5a046',
    icon: <AlertTriangle size={16} />,
  },
  CRITICAL: {
    bg: 'var(--vz-danger-bg-subtle)',
    border: 'rgba(237, 94, 94, 0.5)',
    fg: '#c95050',
    icon: <AlertOctagon size={16} />,
  },
} as const;

function BannerRow({
  banner,
  onDismiss,
}: {
  banner: ActiveBanner;
  onDismiss: () => void;
}) {
  const cfg = SEVERITY_CONFIG[banner.severity];

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '8px 12px',
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: '4px',
        fontSize: '13px',
        lineHeight: 1.4,
        color: 'var(--vz-body-color)',
      }}
    >
      <span style={{ color: cfg.fg, flexShrink: 0, marginTop: '1px' }}>
        {cfg.icon}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600, color: cfg.fg }}>{banner.title}</span>
        <span style={{ marginLeft: '8px' }}>{banner.body}</span>

        {banner.ctaUrl && banner.ctaLabel && (
          <a
            href={banner.ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginLeft: '12px',
              fontWeight: 500,
              color: cfg.fg,
              textDecoration: 'underline',
            }}
          >
            {banner.ctaLabel}
          </a>
        )}
      </div>

      <button
        onClick={onDismiss}
        title="Dismiss"
        aria-label="Dismiss banner"
        style={{
          background: 'transparent',
          border: 'none',
          padding: '2px',
          cursor: 'pointer',
          color: cfg.fg,
          opacity: 0.75,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
